import { Buffer } from 'buffer'
import result from './api/result'
import { apiIdOf } from '@/utils/resource_ids'
import { axiosRetryx } from '@/utils/axios_retryx'
import {
  apiDefaultDescriptions,
  errorDefaultMessage,
  apiMessageOf,
} from '@/utils/errors'
import { getKnackRevision } from '@/utils/kuraemon'
import { useLoadingStore } from "@/stores/loading"
import { useAppErrorStore } from "@/stores/appError"

const API_ORIGIN = import.meta.env.VITE_APP_API_ORIGIN;
const API_CONFIG = {
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
}
const OK = 200;
const WARNING_DISK_CHECK_90 = 250;
const SITE_UPDATE_REQUIRED_CODE = 490;
const CHANGED_PERMISSION_CODE = 493;
const CHANGED_PLAN_CODE = 494;

const SESSION_EXPIRED_CODE = 501;
const IP_ADDRESS_BLOCKING_CODE = 401018;
const KURAEMON_LIVE_ERROR_CODE = 401035;

/**
 * API設定を返す.
 * @param {Object} context 
 * @returns API設定
 */
const apiConfigFor = async (context) => {
  if(!context.getters['xApiKey']){
    await context.dispatch("initXApiKey")
  }
  const xApiKey = context.getters['xApiKey'];

  return {
    ...API_CONFIG,
    headers: {
      ...API_CONFIG.headers,
      'x-api-key': xApiKey,
    },
  }
}

/**
 * API呼び出し.
 * API実行前後でエラー情報を収集する.
 * @param {Object} context 
 * @param {Object} apiOptions
 * @param {Function} callback 
 * @returns callbackの実行結果
 */
const apiCall = async (context, apiOptions, callback) => {
  const loadingStore = useLoadingStore()
  const appErrorStore = useAppErrorStore()
  
  const apiCallProc = async () => {
    try {
      if( !apiOptions.noInitAppError ){
        appErrorStore.updateAppError(null);
      }
      const apiConfig = await apiConfigFor(context)
  
      return await callback(apiConfig)
    } catch(e) {
      const httpStatus = (e.response ? e.response.status : undefined);
      const sysError = { error: e, httpStatus, description: errorDefaultMessage, ...apiOptions }
      appErrorStore.updateAppError(sysError);
      console.warn('system error caught:', e)
    }
  }

  if( apiOptions.noDisplayLoading ){
    return await apiCallProc()
  }

  return loadingStore.loadingProc(async () => {
    return await apiCallProc()
  })
}

/**
 * レスポンスにエラーを含むかどうかを返す.
 * エラーがある場合は、app/appError ステートにエラー情報をセットする.
 * @param {object} context コンテクスト
 * @param {object} response APIレスポンス
 * @param {object} descriptions メッセージ（キー：エラーコード、値：メッセージ）
 * @param {object} apiOptions
 * @param {array} okCodes OKとするコード
 * @returns エラーがある場合はtrue, それ以外はfalse
 */
const checkResponseError = (context, response, descriptions, apiOptions = {}, okCodes = [OK]) => {
  const appErrorStore = useAppErrorStore()
  if (!okCodes.includes(response.data.code)) {
    const description = descriptions[response.data.code] || errorDefaultMessage;
    const siteUpdateRequired = (SITE_UPDATE_REQUIRED_CODE === response.data.code);
    const ipAddressBlocking = (IP_ADDRESS_BLOCKING_CODE === response.data.code);
    const sessionExpired = (SESSION_EXPIRED_CODE === response.data.code);
    const changedPermission = (CHANGED_PERMISSION_CODE === response.data.code);
    const changedPlan = (CHANGED_PLAN_CODE === response.data.code);
    
    // displayContactSupportLinkの判定
    let displayContactSupportLink = false;
    // 401035エラー（kuraemonLive系）の場合は常にtrue
    if (response.data.code === KURAEMON_LIVE_ERROR_CODE) {
      displayContactSupportLink = true;
    }
    // apiOptionsでdisplayContactSupportLinkが明示的に設定されている場合はそれを優先
    if (Object.prototype.hasOwnProperty.call(apiOptions, 'displayContactSupportLink')) {
      displayContactSupportLink = apiOptions.displayContactSupportLink;
    }

    const apiError = {
      apiResponseCode: response.data.code,
      description,
      siteUpdateRequired,
      ipAddressBlocking,
      sessionExpired,
      changedPermission,
      changedPlan,
      displayContactSupportLink,
      ...apiOptions,
    }

    appErrorStore.updateAppError(apiError);
    console.warn('API error:', apiError)
    return true;
  }
  return false;
}

const set = property => (state, payload) => (state[property] = payload)
const get = property => (state) => (state[property])

export default {
  namespaced: true,
  modules: {
    result,
  },
  state: {
    xApiKey: null,
  },
  getters: {
    xApiKey: get('xApiKey'),
  },
  mutations: {
    setXApiKey: set('xApiKey'),
  },
  actions: {
    /** x-api-keyの初期化 */
    async initXApiKey(context) {
      const xorDecode = (base64Encoded) => {
        const encoded = Buffer.from(base64Encoded, 'base64').toString();
        const key = 'WjXQbn1C2hU66RFmb6xME5BSadsxK6khBSDjrFby';
        return Array.from(
          encoded,
          (c, i) => String.fromCharCode(c.charCodeAt() ^ key.charCodeAt(i % key.length))
        ).join('');
      }
      context.commit('setXApiKey', xorDecode(import.meta.env.VITE_APP_ENCRYPTED_X_API_KEY))
    },
    /** アカウント仮登録情報取得API */
    async getTemporaryAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTemporaryAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettemporaryaccount";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        // 無効な仮登録情報の場合のコード
        const EXPIRED_TEMPORARY_ACCOUNT_CODE = 401;
        const REAPPLYABLE_TEMPORARY_ACCOUNT_CODE = 402;
        const codes = [OK, EXPIRED_TEMPORARY_ACCOUNT_CODE, REAPPLYABLE_TEMPORARY_ACCOUNT_CODE];
        if (checkResponseError(context, response, descriptions, apiOptions, codes )) {
          return;
        }
        if(EXPIRED_TEMPORARY_ACCOUNT_CODE === response.data.code){
          return 'EXPIRED_TEMPORARY_ACCOUNT';
        }
        if(REAPPLYABLE_TEMPORARY_ACCOUNT_CODE === response.data.code){
          return 'REAPPLYABLE_TEMPORARY_ACCOUNT';
        }

        const apiResult = {
          mail: response.data.mail,
          app_user_name: response.data.app_user_name,
          contact_tel: response.data.contact_tel,
          register_route: response.data.register_route,
          company_name: response.data.company_name,
          department_name: response.data.department_name,
        }
        
        context.commit('result/setTemporaryAccountInfo', apiResult);
        return apiResult;
      })
    },
    /** ユーザーアイコンアップロードURL取得API */
    async getAccountIconUploadUrl(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAccountIconUploadUrl');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getaccounticonuploadurl";
        const data = {
          file_info: args.file_info,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          icon_id: response.data.icon_id,
          url: response.data.url,
        }
        return apiResult;
      })
    },
    /** アカウント本登録_起動API */
    async registerAccount1(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerAccount1');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeraccount1";
        const data = {
          onetime_token: args.onetime_token,
          mail: args.mail,
          password: args.password,
          account_name: args.account_name,
          contact_tel: args.contact_tel,
          company_name: args.company_name,
          department_name: args.department_name,
          industry_name: args.industry_name,
        }
        if(args.icon_id) {
          data.icon_id = args.icon_id;
        } else {
          data.icon_path = args.icon_path;
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const EXPIRED_TOKEN_CODE = 402;
        const descriptions = {
          ...apiDefaultDescriptions,
          401: `パスワードは8文字以上で
英大文字、英小文字、数字を含む必要があります`,
          [EXPIRED_TOKEN_CODE]: `URLの有効期限が切れています
再度登録手続きを行ってください`,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          if(response.data.code === EXPIRED_TOKEN_CODE){
            // トークン期限切れ
            return 'EXPIRED_TOKEN';
          }
          return;
        }
        const apiResult = {
          job_code: response.data.job_code,
          polling_millisec: response.data.polling_millisec,
        }
        return apiResult;
      })
    },
    /** アカウント本登録_確認API */
    async registerAccount2(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerAccount2');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeraccount2";
        const data = {
          job_code: args.job_code,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const INVITED_OK_CODE = 201;
        const CONTINUE_CODE = 201000;
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, INVITED_OK_CODE, CONTINUE_CODE])) {
          return;
        }
        if(response.data.code === CONTINUE_CODE){
          // 処理中
          const apiResult = {
            job_code: response.data.job_code,
            polling_millisec: response.data.polling_millisec,
          }
          return apiResult;
        }
        // 登録完了
        context.commit('result/setRegisteredAccountInfo', response.data.account_info);
        if(response.data.code === INVITED_OK_CODE){
          return 'INVITED_OK';
        }
        return 'OK';
      })
    },
    /** アカウント仮登録要求API */
    async registerTemporaryAccountRequest(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerTemporaryAccountRequest');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registertemporaryaccountrequest";
        const data = {
          account_slot_code: args.account_slot_code,
          mail: args.mail,
          user_name: args.user_name,
          company_name: args.company_name,
          company_tel: args.company_tel,
          company_postno: args.company_postno,
          company_prefecture: args.company_prefecture,
          company_address: args.company_address,
          file_md5: args.file_md5,
          request_random_value: args.request_random_value,
          contact_tel: args.contact_tel,
          regist_device: args.regist_device,
          regist_route: args.regist_route,
          invite_share: args.invite_share,
          request_code: args.request_code,
          is_sole_proprietor: args.is_sole_proprietor,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const DUPLICATE_MAILADDR = 402;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, DUPLICATE_MAILADDR])) {
          return;
        }
        if(response.data.code === DUPLICATE_MAILADDR){
          return 'DUPLICATE_MAILADDR';
        }

        return 'OK';
      })
    },
    /** 期限切れアカウント本登録再申請API */
    async reapplyExpiredAccountRequest(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('reapplyExpiredAccountRequest');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/reapplyexpiredaccountrequest";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const ALREADY_APPLIED = 400001;
        const REGISTRATION_COMPLETED = 400002;
        const codes = [OK, ALREADY_APPLIED, REGISTRATION_COMPLETED]
        if (checkResponseError(context, response, descriptions, apiOptions, codes)) {
          return;
        }
        if(response.data.code === ALREADY_APPLIED){
          return 'ALREADY_APPLIED';
        }
        if(response.data.code === REGISTRATION_COMPLETED){
          return 'REGISTRATION_COMPLETED';
        }
        return 'OK';
      })
    },
    /** 招待/データ共有内容取得API */
    async getInviteShareInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getInviteShareInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getinviteshareinfo";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.invite_share_info;
      })
    },
    /** トークンからアカウント登録情報取得API */
    async getTemporaryAccountRequestInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTemporaryAccountRequestInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettemporaryaccountrequestinfo";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const TOKEN_EXPIRED = 401009
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, TOKEN_EXPIRED])) {
          return;
        }
        if (response.data.code === TOKEN_EXPIRED) return 'TOKEN_EXPIRED'
        const apiResult = {
          temporary_account_request_info: response.data.temporary_account_request_info,
          condition: response.data.condition,
        }
        return apiResult;
      })
    },
    /** トークンからアカウント仮登録要求API */
    async registerTemporaryAccountRequestWithToken(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerTemporaryAccountRequestWithToken');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registertemporaryaccountrequestwithtoken";
        const data = {
          onetime_token: args.onetime_token,
          mail: args.mail,
          user_name: args.user_name,
          company_name: args.company_name,
          company_tel: args.company_tel,
          company_postno: args.company_postno,
          company_prefecture: args.company_prefecture,
          company_address: args.company_address,
          contact_tel: args.contact_tel,
          request_random_value: args.request_random_value,
          is_sole_proprietor: args.is_sole_proprietor,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const errorDefs = {
          400041: 'DUPLICATE_MAILADDR', // 重複メールアドレスのエラーコード
          401009: 'TOKEN_EXPIRED',      // ワンタイムトークン期限切れエラーコード
        }
        const codes = [OK, ...Object.keys(errorDefs)].map(cd => Number(cd))
        if (checkResponseError(context, response, descriptions, apiOptions, codes)) {
          return;
        }
        const theError = errorDefs[response.data.code]
        if (theError) return theError
        return 'OK';
      })
    },
    /** パスワード再設定メール送信API */
    async sendAccountPasswordResetMail(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('sendAccountPasswordResetMail');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/sendaccountpasswordresetmail";
        const data = {
          mail: args.mail,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401: `メールアドレスが間違っている
または登録がされていません`,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** パスワード再設定情報取得API */
    async getResetPasswordAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getResetPasswordAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getresetpasswordaccount";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const EXPIRED_TOKEN_CODE = 401;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
          return; // エラー
        }
        if(response.data.code === EXPIRED_TOKEN_CODE){
          return "EXPIRED_TOKEN";
        }
        return response.data.account_info;
      })
    },
    /** パスワード再設定API */
    async resetAccountPassword(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('resetAccountPassword');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/resetaccountpassword";
        const data = {
          onetime_token: args.onetime_token,
          password: args.password,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          403: `パスワードは8文字以上で
英大文字、英小文字、数字を含む必要があります`,
        }
        const EXPIRED_TOKEN_CODE = 401;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
          return; // エラー
        }
        if(response.data.code === EXPIRED_TOKEN_CODE){
          return "EXPIRED_TOKEN";
        }
        return "OK"
      })
    },
    /** アカウントログインAPI */
    async loginAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginaccount";
        const data = {
          mail: args.mail,
          password: args.password,
          onetime_password: args.onetime_password,
          app_type: args.app_type,
          app_name: args.app_name,
          is_forced: args.is_forced,
          logout_session_id: args.logout_session_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig }).catch(e => {
          const httpStatus = (e.response ? e.response.status : undefined);
          if(httpStatus && httpStatus === 429){
            context.dispatch("app/updateAppError", { tooManyRequestsError: true }, { root: true })
            return;
          }
          throw e;
        })
        if(!response) return;

        // 別端末でログイン済み（同時ログイン制限）
        const loginErrorCode = 401;
        const duplicateSessionErrorCode = 403;
        const mfaRequiredErrorCode = 201001;

        const checkSettings = (() => {
          if (args.onetime_password) {
            // ワンタイムパスワード指定の場合
            return {
              descriptions: {
                ...apiDefaultDescriptions,
                402: '',
              },
              okCodes: [OK, duplicateSessionErrorCode, loginErrorCode],
            }
          }
          // 通常のログイン
          return {
            descriptions: {
              ...apiDefaultDescriptions,
              402: '',
            },
            okCodes: [OK, loginErrorCode, duplicateSessionErrorCode, mfaRequiredErrorCode],
          }
        })()
        
        if (checkResponseError(context, response, checkSettings.descriptions, apiOptions, checkSettings.okCodes)) {
          return;
        }

        if (!args.onetime_password && response.data.code === loginErrorCode) {
          context.dispatch("app/updateAppError", {
            apiResponseCode: loginErrorCode,
            description: `ログインできませんでした
                          メールアドレスまたはパスワードが間違っています
                          登録内容をご確認の上、再度ご入力ください`,
            loginError: true,
            ...apiOptions,
          }, { root: true })
          return
        }

        const isTfaEnabled = response.data.is_tfa_enabled
        if(response.data.code === loginErrorCode){
          // ワンタイムパスワードエラー
          return { loginResult: 'OTP_FAILED', isTfaEnabled }
        }
        if(response.data.code === mfaRequiredErrorCode){
          // 多要素認証が必要
          if(response.data.auth_info) {
            return { loginResult: 'MFA_REQUIRED_FIRST', authInfo: response.data.auth_info, isTfaEnabled }
          }
          return { loginResult: 'MFA_REQUIRED', isTfaEnabled }
        }

        if(response.data.code === duplicateSessionErrorCode){
          // 別端末でログイン済み（同時ログイン制限）時のAPP情報
          context.commit('result/setLoginAppInfoList', response.data.login_app_info_list);
          return { loginResult: 'DUPLICATE_SESSION', isTfaEnabled }
        }

        // 工事ごとのコンテンツ保管場所のクリア
        await context.dispatch('page/loginCompleted', response.data, { root: true })

        // トークお知らせURLをセット
        context.commit('result/setTalkNotificationUrls', {
          first: response.data.talk_notification_first_request_url,
          next: response.data.talk_notification_next_request_url,
        })

        if(response.data.show_license_input){
          return { loginResult: 'OK', showLicenseInput: true, isTfaEnabled }
        }

        return { loginResult: 'OK', isTfaEnabled }
      })
    },
    /** アカウント2要素認証設定登録API */
    async registerAccountTfaSetting(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerAccountTfaSetting');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeraccounttfasetting";
        const data = {
          mail: args.mail,
          password: args.password,
          onetime_password: args.onetime_password,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const otpErrorCode = 400027
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, otpErrorCode])) {
          return;
        }
        if (response.data.code === otpErrorCode) {
          return "REGISTER_NG"
        }
        return 'OK';
      })
    },
    /** 連携ログイン情報取得 */
    async getCooperationInfos(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getCooperationInfos');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcooperationinfos";
        const data = {
          app_name: args.app_name,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        console.info('response', response)
        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }

        const apiResult = {
          xgrab_info: response.data.xgrab_info,
        }

        return apiResult
      })
    },

    /** メールアドレスから連携ログインを取得 */
    async getSsoMethod(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getSsoMethod');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getssomethod";
        const data = {
          email:args.email,
          app_name: args.app_name,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const loginErrorMsg = `ログインできませんでした
        メールアドレスが間違っています
        登録内容をご確認の上、再度ご入力ください`
        const descriptions = {
          ...apiDefaultDescriptions,
          401: loginErrorMsg,
        }
        const METHOD_NOT_FOUND_CODE = 400050
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, METHOD_NOT_FOUND_CODE])) {
          return;
        }
        if (response.data.code === METHOD_NOT_FOUND_CODE) {
          const apiResult = {
            sso_methods: [],
          }
          return apiResult
        }

        const apiResult = {
          sso_methods: response.data.sso_methods,
          is_new_account: response.data.is_new_account,
        }
        return apiResult
      })
    },

    /** xgarb連携ログインAPI,xgrabで発行されたAuthCodeを利用してログインする */
    async loginXgrabAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginXgrabAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginxgrabaccount";
        const data = {
          auth_code: args.auth_code,
          code_verifier: args.code_verifier,
          app_name: args.app_name,
          device_name: args.device_name,
        }
        if (args.device_id) {
          data.device_id = args.device_id
        }
        if (args.project_id) {
          data.project_id = args.project_id
        }
        if (args.forced_param) {
          data.forced_param = args.forced_param
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const duplicateSessionErrorCode = 403; // 追い出しログインが必要な場合
        const descriptions = {
          ...apiDefaultDescriptions,
          400049: `大成建設様とのご契約ライセンスの上限に達しました
                  恐れ入りますが、お近くのICTキャラバン隊
                  または蔵衛門担当の大前香苗（oomkne00@pub.taisei.co.jp）までご連絡ください`, // ライセンスマックスのエラー 
          400052: `ログインに失敗しました
                  蔵衛門担当の大前香苗（oomkne00@pub.taisei.co.jp）までご連絡ください`,
        }
        const okCodes = [OK, duplicateSessionErrorCode]

        if (checkResponseError(context, response, descriptions, apiOptions, okCodes)) {
          return;
        }

        if(response.data.code === duplicateSessionErrorCode){
          // 別端末でログイン済み（同時ログイン制限）時のAPP情報
          context.commit('result/setLoginAppInfoList', response.data.login_app_info_list);
          return { loginResult: 'DUPLICATE_SESSION' }
        }

        // 工事ごとのコンテンツ保管場所のクリア
        await context.dispatch('page/loginCompleted', response.data, { root: true })

        // トークお知らせURLをセット
        context.commit('result/setTalkNotificationUrls', {
          first: response.data.talk_notification_first_request_url,
          next: response.data.talk_notification_next_request_url,
        })

        if (response.data.show_license_input) {
          return { loginResult: 'OK', code: response.data.code, showLicenseInput: true }
        }

        if (response.data.login_cloud_construction_id) {
          return { loginResult: 'OK', code: response.data.code, login_cloud_construction_id: response.data.login_cloud_construction_id }
        }

        return { loginResult: 'OK', code: response.data.code }
      })
    },
    /** (アプリ用)xgarb連携ログインAPI */
    async loginXgrabAccount_ForApp(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginXgrabAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginxgrabaccount";
        const data = {
          auth_code: args.auth_code,
          code_verifier: args.code_verifier,
          app_name: args.app_name,
          app_unique_id: args.app_unique_id,
          app_user_id: args.app_user_id,
          app_user_name: args.app_user_name,
          app_version: args.app_version,
          interface_revision: args.interface_revision,
          device_name: args.device_name,
        }
        if (args.device_id) {
          data.device_id = args.device_id
        }
        if (args.project_id) {
          data.project_id = args.project_id
        }
        if (args.forced_param) {
          data.forced_param = args.forced_param
        }
        console.info('loginXgrabAccount_ForApp axios', data)
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        return response.data
      })
    },
    /** SSOログインAPI */
    async loginWithSso(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginWithSso');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginwithsso";
        const data = {
          sso_token: args.sso_token,
          app_name: args.app_name,
        }
        if (args.forced_param) {
          data.forced_param = args.forced_param
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const duplicateSessionErrorCode = 403; // 追い出しログインが必要な場合
        const descriptions = {
          ...apiDefaultDescriptions,
          400056: `ログインに失敗しました`,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, duplicateSessionErrorCode])) {
          return;
        }

        if (response.data.code === duplicateSessionErrorCode) {
          // 別端末でログイン済み（同時ログイン制限）時のAPP情報
          context.commit('result/setLoginAppInfoList', response.data.login_app_info_list);
          return { loginResult: 'DUPLICATE_SESSION', code: response.data.code }
        }

        if (response.data.app_return_url) {
          // アプリへの戻り先URLが指定されている場合
          return { loginResult: 'APPLICATION_RETURN', code: response.data.code, app_return_url: response.data.app_return_url };
        }

        // 工事ごとのコンテンツ保管場所のクリア
        await context.dispatch('page/loginCompleted', response.data, { root: true })

        // トークお知らせURLをセット
        context.commit('result/setTalkNotificationUrls', {
          first: response.data.talk_notification_first_request_url,
          next: response.data.talk_notification_next_request_url,
        })

        return { loginResult: 'OK', code: response.data.code }
      })
    },
    /** (アプリ用)SSOログインAPI */
    async loginWithSso_ForApp(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginWithSso');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginwithsso";
        const data = {
          sso_token: args.sso_token,
          app_name: args.app_name,
          app_unique_id: args.app_unique_id,
          app_user_id: args.app_user_id,
          app_user_name: args.app_user_name,
          app_version: args.app_version,
          interface_revision: args.interface_revision,
          device_name: args.device_name,
          device_id: args.device_id,
        }
        if (args.forced_param) {
          data.forced_param = args.forced_param
        }
        console.info('loginWithSso_ForApp axios', data)
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        return response.data
      })
    },
    /** 管理画面からのアカウント強制ログインAPI */
    async loginKobito(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('loginKobito');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/loginkobito";
        const data = {
          kobitoToken: args.kobitoToken,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const loginErrorMsg = `ログインできませんでした`
        const descriptions = {
          ...apiDefaultDescriptions,
          401: loginErrorMsg,
          402: '',
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }

        // 工事ごとのコンテンツ保管場所のクリア
        await context.dispatch('page/loginCompleted', response.data, { root: true })

        // トークお知らせURLをセット
        context.commit('result/setTalkNotificationUrls', {
          first: response.data.talk_notification_first_request_url,
          next: response.data.talk_notification_next_request_url,
        })

        return 'OK'
      })
    },
    /** アカウントログインチェックAPI */
    async checkLoginAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkLoginAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checkloginaccount";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        // 501エラーの場合、ダイアログ表示ありに設定する
        if(response.data.code === SESSION_EXPIRED_CODE){
          apiOptions.noErrorDialog = false
        }

        // 401018エラーの場合、ダイアログ表示ありに設定する
        if(response.data.code === IP_ADDRESS_BLOCKING_CODE){
          apiOptions.noErrorDialog = false
        }
                
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          context.commit('result/setAccountInfo', null);
          return;
        }
        const apiResult = response.data.account_info;
        context.commit('result/setAccountInfo', apiResult);

        // client_key_prefix を更新
        context.dispatch("itemKeys/updateClientKeyPrefix",
          response.data.client_key_prefix, { root: true })

        // トークお知らせURLをセット
        context.commit('result/setTalkNotificationUrls',{
          first: response.data.talk_notification_first_request_url,
          next: response.data.talk_notification_next_request_url,
        })

        return apiResult;
      })
    },
    /** アカウント仮登録要求状況確認API */
    async getTemporaryAccountRequestStatus(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTemporaryAccountRequestStatus');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettemporaryaccountrequeststatus";
        const data = {
          account_slot_code: args.account_slot_code,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.status_info;
      })
    },
    /** アカウント情報取得API */
    async getAccountInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAccountInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getaccountinfo";
        const data = {
          account_id: args.account_id,
          request_current_disk_byte: args.request_current_disk_byte,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.account_info;
        context.commit('result/setAccountDetailInfo', apiResult);
        return apiResult;
      })
    },
    /** アカウント情報更新API */
    async updateAccountInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updateAccountInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/updateaccountinfo";
        const data = {
          update_items: args.update_items,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.update_cloud_account_info;
        // 編集中のアカウント情報をマージ
        context.commit('result/mergeAccountDetailInfo', apiResult);
        // 現在のログインアカウントの情報を更新
        context.commit('result/updateCurrentAccountInfo', apiResult);
        return apiResult;
      })
    },
    /** メールアドレス変更API */
    async changeMailAddress(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('changeMailAddress');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/changemailaddress";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const EXPIRED_TOKEN_CODE = 401;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
          return; // エラー
        }
        if(response.data.code === EXPIRED_TOKEN_CODE){
          return "EXPIRED_TOKEN";
        }
        return 'OK'
      })
    },
    /** パスワード変更API */
    async changePassword(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('changePassword');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/changepassword";
        const data = {
          old_password: args.old_password,
          new_password: args.new_password,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** アカウントライセンス登録API */
    async registerAccountLicense(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerAccountLicense');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeraccountlicense";
        const data = {
          license_code: args.license_code,
          license_register_route: args.license_register_route,
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const licenseErrors = {
          401: 'INVALID_LICENSE',
          402: 'EXPIRED_LICENSE',
          410: 'EXCEEDED_PERIOD',
          403: 'USED_LICENSE',
          404: 'EXCEEDED_COUNT',
          405: 'INVALID_ACCOUNT_ADMIN_RULE_ENTERPRIZE',
          406: 'INVALID_ACCOUNT_BUSINESS_RULE_ENTERPRIZE',
          407: 'USED_LICENSE_ENTERPRIZE',
          400038: 'PACK_LICENSE_APPLIED_TO_SAME_LICENSED_ACCOUNT',
        };
        const codes = [OK, ...Object.keys(licenseErrors)].map(cd => Number(cd))
        if (checkResponseError(context, response, descriptions, apiOptions, codes)) {
          return;
        }
        const licenseError = licenseErrors[response.data.code]
        if(licenseError){
          return { license_error: licenseError };
        }

        const apiResult = {
          account_service_contract_info: response.data.account_service_contract_info,
        }
        return apiResult;
      })
    },
    /** アカウントライセンス情報取得API */
    async getAccountLicense(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAccountLicense');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getaccountlicense";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          account_service_contract_info: response.data.account_service_contract_info,
          next_account_service_contract_info: response.data.next_account_service_contract_info,
        }
        context.commit('result/setAccountLicenseInfo', apiResult);
        return apiResult;
      })
    },
    /** 運用管理画面ログインURL取得API */
    async getEnterpriseManageLoginUrl(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getEnterpriseManageLoginUrl');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getenterprisemanageloginurl";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.url;
      })
    },
    /** アカウントログアウトAPI */
    async logoutAccount(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('logoutAccount');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/logoutaccount";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        context.commit('setXApiKey', null)
        context.commit('result/init');
        return 'OK';
      })
    },
    /** トークン有効期限確認API */
    async checkTokenActive(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkTokenActive');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checktokenactive";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const hasError = checkResponseError(context, response, descriptions, apiOptions);
        const activeToken = !hasError;
        context.commit('result/setActiveOnetimeToken', { onetimeToken: args.onetime_token, active: activeToken })
        return activeToken;
      })
    },
    /** バナー情報取得API */
    async getBannerInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getBannerInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getbannerinfo";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.banner_info;
      })
    },
    /** クラウド工事作成API */
    async createCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createcloudconstruction";
        const data = {
          cloud_construction_name: args.cloud_construction_name,
          cloud_construction_knack_id: args.cloud_construction_knack_id,
          is_usable_cloud_album_share: args.is_usable_cloud_album_share,
        }
        if (args.source_construction_id) {
          data.source_construction_id = args.source_construction_id
        }
        if(args.organize_info_tree){
          data.organize_info_tree = args.organize_info_tree
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.cloud_construction_info;
      })
    },
    /** クラウド工事メンバー追加API */
    async addMemberToCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('addMemberToCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/addmembertocloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          mail: args.mail,
          embedding_param: args.embedding_param,
          account_id: args.account_id,
          role_type: args.role_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401001: apiMessageOf(401001),
          401004: apiMessageOf(401004),
        }

        const MEMBER_EXISTS_CODE = 201;
        const FREEMAIL_DOMAIN_CODE = 407;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, MEMBER_EXISTS_CODE, FREEMAIL_DOMAIN_CODE])) {
          return; // エラー
        }
        if(response.data.code === MEMBER_EXISTS_CODE){
          return "MEMBER_EXISTS";
        }
        if(response.data.code === FREEMAIL_DOMAIN_CODE){
          return "FREEMAIL_DOMAIN";
        }
        return 'OK';
      })
    },
    /** クラウド工事メンバー複数追加API */
    async bulkAddMemberMailToCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('bulkAddMemberMailToCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/bulkaddmembermailtocloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          mail_list: args.mail_list,
          embedding_param: args.embedding_param,
          role_type: args.role_type,
          invite_route: args.invite_route,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const INVITED_OR_INVALID_MAIL_ERROR = 200002
        const okCodes = [OK, INVITED_OR_INVALID_MAIL_ERROR]
        if (checkResponseError(context, response, descriptions, apiOptions, okCodes)) {
          return;
        }
        const account_info_list = response.data.account_info_list
        if(response.data.code === INVITED_OR_INVALID_MAIL_ERROR
          && account_info_list && 0 < account_info_list.length){
          // すでに招待済み、または、利用不可のメールアドレスが指定された
          return {
            error: 'INVITED_OR_INVALID_MAIL_ERROR',
            account_info_list,
          }
        }
        return 'OK'
      })
    },
    /** 工事追加メンバーチェックAPI */
    async checkAddMemberMail(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkAddMemberMail');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checkaddmembermail";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          mail: args.mail,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        const CONSTRUCTION_MEMBER_MAIL = 200001
        const INVALID_MAIL_ERROR = 401022
        const FREE_MAIL_ERROR = 400015
        const okCodes = [OK, CONSTRUCTION_MEMBER_MAIL, INVALID_MAIL_ERROR, FREE_MAIL_ERROR]
        if (checkResponseError(context, response, descriptions, apiOptions, okCodes)) {
          return;
        }
        if(response.data.code === INVALID_MAIL_ERROR){
          return { error: 'INVALID_MAIL_ERROR' }
        }
        if(response.data.code === FREE_MAIL_ERROR){
          return { error: 'FREE_MAIL_ERROR' }
        }
        if(response.data.code === CONSTRUCTION_MEMBER_MAIL){
          return {
            error: 'CONSTRUCTION_MEMBER_MAIL',
            accound_id: response.data.account_id,
          }
        }
        return 'OK';
      })
    },
    /** 工事追加複数メンバーチェックAPI */
    async checkAddMemberMultipleMail(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkAddMemberMultipleMail');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checkaddmembermultiplemail";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          mail_list: args.mail_list,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.mail_check_result_list;
      })
    },
    /** クラウド工事メンバー削除API */
    async deleteMemberToCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('deleteMemberToCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/deletemembertocloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          delete_account_id: args.delete_account_id,
          embedding_param: args.embedding_param,
          is_last_manager_deletion_confirmed: args.is_last_manager_deletion_confirmed,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const PIX_ACCOUNT_LEFT_NG_CODE = 408;
        const CAM_ACCOUNT_LEFT_NG_CODE = 400025;
        const LAST_MANAGER_DELETING_NG_CODE = 409;
        const BOOKRACK_EDITOR_LEFT_NG_CODE = 401026;
        const descriptions = {
          ...apiDefaultDescriptions,
          401002: `メンバーを削除する権限がありません
          工事の管理者に権限を付与するようご連絡ください`,
        }

        if (checkResponseError(context, response, descriptions, apiOptions, [OK, PIX_ACCOUNT_LEFT_NG_CODE, CAM_ACCOUNT_LEFT_NG_CODE, LAST_MANAGER_DELETING_NG_CODE, BOOKRACK_EDITOR_LEFT_NG_CODE])) {
          return;
        }
        if(response.data.code === PIX_ACCOUNT_LEFT_NG_CODE){
          return "PIX_ACCOUNT_LEFT_NG";
        }
        if(response.data.code === CAM_ACCOUNT_LEFT_NG_CODE){
          return "CAM_ACCOUNT_LEFT_NG";
        }
        if(response.data.code === LAST_MANAGER_DELETING_NG_CODE){
          return "LAST_MANAGER_DELETING_NG";
        }
        if(response.data.code === BOOKRACK_EDITOR_LEFT_NG_CODE){
          return "BOOKRACK_EDITOR_LEFT_NG";
        }
        if(context.rootGetters['api/result/accountInfo'].account_id === args.delete_account_id){
          // ログインユーザーを工事から削除する場合、指定工事のデータを削除する
          context.dispatch('contents/deleteConstruction', args.cloud_construction_id, { root: true })
        }
        return 'OK'
      })
    },
    /** 工事メンバー権限変更API */
    async changeMemberPermissions(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('changeMemberPermissions');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/changememberpermissions";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          account_id: args.account_id,
          role_type: args.role_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const LAST_MANAGER_ERR_CODE = 408;
        const BOOKRACK_EDITOR_ERR_CODE = 401026;
        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if(checkResponseError(context, response, descriptions, apiOptions, [OK, LAST_MANAGER_ERR_CODE, BOOKRACK_EDITOR_ERR_CODE])){
          return;
        }
        if(response.data.code === LAST_MANAGER_ERR_CODE){
          return "LAST_MANAGER_ERR";
        }
        if(response.data.code === BOOKRACK_EDITOR_ERR_CODE){
          return "BOOKRACK_EDITOR_ERR"
        }
        return 'OK'
      })
    },
    /** 招待工事トークン解決API */
    async canJoinInvitedCloudConstructionMember(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('canJoinInvitedCloudConstructionMember');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/canjoininvitedcloudconstructionmember";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401: `工事招待メールに記載されているリンクの有効期限が切れています
                工事に参加しているメンバーに再度招待を依頼し、
                送られてきた新しいメールのリンクより工事に参加してください`
        }

        // 工事参加が制限されている戻り値コード
        const forbiddenCode = 201;
        // 招待されていないユーザーが開いた時のエラー
        const wrongUserErrorCode = 401005;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, forbiddenCode, wrongUserErrorCode])) {
          return;
        }
        if(forbiddenCode === response.data.code){
          const apiResult = {
            invited_cloud_construction_name: response.data.invited_cloud_construction_name,
            joining_cloud_construction_name: response.data.joining_cloud_construction_name,
          }
          context.commit('result/setParticipatingConstruction', apiResult);
          return apiResult;
        }
        if(wrongUserErrorCode === response.data.code){
          return "WRONG_USER_ERROR";
        }
        const apiResult = {
          cloud_construction_id: response.data.cloud_construction_id,
          invite_route: response.data.invite_route
        }
        return apiResult;
      })
    },
    /** 招待工事だけにするAPI */
    async joinCloudConstructionMemberOnlyInvite(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('joinCloudConstructionMemberOnlyInvite');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/joincloudconstructionmemberonlyinvite";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        
        // 招待されていないユーザーが開いた時のエラー
        const wrongUserErrorCode = 401005;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, wrongUserErrorCode])) {
          return;
        }
        if(wrongUserErrorCode === response.data.code){
          return "WRONG_USER_ERROR";
        }
        return {
          cloud_construction_id: response.data.cloud_construction_id
        }
      })
    },
    /** クラウド工事情報取得API */
    async getCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          knack_revision: getKnackRevision(),
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        /** @type {{cloud_construction_info: *, s3_info: *}} */
        const apiResult = response.data;
        if(!apiOptions.noStore){
          // 結果をstoreに保存する指定の場合（デフォルト）
          context.commit('result/setCloudConstructionInfo', apiResult.cloud_construction_info);
        }
        
        return apiResult;
      })
    },
    /** @deprecated Pinia の useConstructionStore.updateCloudConstruction を使用してください */
    async updateCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updateCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/updatecloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          update_items: args.update_items,
          construction_settings: args.construction_settings,
          kokuban_settings: args.kokuban_settings,
          is_restricted_member_join: args.is_restricted_member_join,
          management_cloud_construction_name: args.management_cloud_construction_name,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.update_cloud_construction_info;
      })
    },
    /** 黒板一覧取得API */
    async getKokubanList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKokubanList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkokubanlist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          latest_modified_at: args.latest_modified_at,
          exclusive_start_key: args.exclusive_start_key,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          kokuban_count: response.data.kokuban_count,
          kokuban_list: response.data.kokuban_list,
          last_evaluated_key: response.data.last_evaluated_key,
          latest_modified_at: response.data.latest_modified_at,
        }
        
        return apiResult;
      })
    },
    /** 写真一覧取得API */
    async getPhotoList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getPhotoList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getphotolist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          latest_modified_at: args.latest_modified_at,
          exclusive_start_key: args.exclusive_start_key,
          known_account_ids: args.known_account_ids,
          client_supported_data_kind: "webUpload",
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          photo_count: response.data.photo_count,
          photo_list: response.data.photo_list,
          account_info_list: response.data.account_info_list,
          last_evaluated_key: response.data.last_evaluated_key,
          latest_modified_at: response.data.latest_modified_at,
        }
        return apiResult;
      })
    },
    /** クラウド工事データ共有API */
    async shareCloudConstructionData(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('shareCloudConstructionData');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/sharecloudconstructiondata";
        // 共有メッセージ
        const share_message = (() => {
          if(args.share_message) return args.share_message;
          if(!args.embedding_param) return undefined;
          return args.embedding_param.user_message;
        })();
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          data_type: args.data_type,
          data_list: args.data_list,
          share_user_type: args.share_user_type,
          is_include_to_all: args.is_include_to_all,
          share_user_list: args.share_user_list,
          share_message,
          embedding_param: args.embedding_param,
          role_type: args.role_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401001: apiMessageOf(401001),
          401004: apiMessageOf(401004),
        }

        const FREEMAIL_DOMAIN_CODE = 407;
        const CURRENT_MEMBER_MAIL_CODE = 201;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, FREEMAIL_DOMAIN_CODE, CURRENT_MEMBER_MAIL_CODE])) {
          return;
        }
        if(response.data.code === FREEMAIL_DOMAIN_CODE){
          return "FREEMAIL_DOMAIN";
        }
        if(response.data.code === CURRENT_MEMBER_MAIL_CODE){
          return "CURRENT_MEMBER_MAIL";
        }
        const apiResult = {
          account_info: response.data.account_info,
          talk_wakeup_url: response.data.talk_wakeup_url,
        }
        return apiResult;
      })
    },
    /** クラウド工事データ複数ユーザー共有API（web用） */
    async bulkShareMailCloudConstructionData(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('bulkShareMailCloudConstructionData');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/bulksharemailcloudconstructiondata";
        // 共有メッセージ
        const share_message = (() => {
          if(args.share_message) return args.share_message;
          if(!args.embedding_param) return undefined;
          return args.embedding_param.user_message;
        })();
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          data_type: args.data_type,
          data_list: args.data_list,
          share_mail_list: args.share_mail_list,
          share_message,
          embedding_param: args.embedding_param,
          role_type: args.role_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401001: apiMessageOf(401001),
          401004: apiMessageOf(401004),
        }

        const INVITED_OR_INVALID_MAIL_ERROR = 200002
        const okCodes = [OK, INVITED_OR_INVALID_MAIL_ERROR]
        if (checkResponseError(context, response, descriptions, apiOptions, okCodes)) {
          return;
        }
        const account_info_list = response.data.account_info_list
        if(response.data.code === INVITED_OR_INVALID_MAIL_ERROR
          && account_info_list && 0 < account_info_list.length){
          // すでに招待済み、または、利用不可のメールアドレスが指定された
          return {
            error: 'INVITED_OR_INVALID_MAIL_ERROR',
            account_info_list,
          }
        }
        return "OK";
      })
    },
    /** クラウド工事参加申請受付確認API */
    async receiveRequestJoinCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('receiveRequestJoinCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/receiverequestjoincloudconstruction";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data;
      })
    },
    /** クラウド工事参加承認API */
    async confirmJoinCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('confirmJoinCloudConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/confirmjoincloudconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          account_id: args.account_id,
          is_accept: args.is_accept,
          role_type: args.role_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const ALREADY_PROCESSED_CODE = 201;
        const descriptions = {
          ...apiDefaultDescriptions,
          401001: apiMessageOf(401001),
          401004: apiMessageOf(401004),
        }

        if (checkResponseError(context, response, descriptions, apiOptions, [OK, ALREADY_PROCESSED_CODE])) {
          return;
        }
        if(response.data.code === ALREADY_PROCESSED_CODE){
          return "ALREADY_PROCESSED";
        }
        return 'OK';
      })
    },
    /** クラウド工事QRコード作成API */
    async createCloudConstructionQrcode(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createCloudConstructionQrcode');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createcloudconstructionqrcode";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          qrcode_data_url: response.data.qrcode_data_url,
          qrcode_data: response.data.qrcode_data,
        }
        
        context.commit('result/setCloudConstructionQrCode', apiResult);
        return apiResult;
      })
    },
    /** クラウド工事QRコードWEB情報取得API */
    async getCloudConstructionQrcodeWebInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getCloudConstructionQrcodeWebInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcloudconstructionqrcodewebinfo";
        const data = {
          onetime_token: args.onetime_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          qrcode_data_url: response.data.qrcode_data_url,
          cloud_construction_id: response.data.cloud_construction_id,
          cloud_construction_name: response.data.cloud_construction_name,
        }
        
        context.commit('result/setCloudConstructionQrCode', apiResult);
        return apiResult;
      })
    },
    /** 写真群取得API */
    async getPhotoSet(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getPhotoSet');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getphotoset";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_keys: args.photo_keys,
          client_supported_data_kind: "webUpload",
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.photo_info_list;
      })
    },
    /** 図面キャプチャによって生成された写真作成API */
    async createPhotoZumenCapture(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createPhotoZumenCapture');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createphotozumencapture";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_info: args.photo_info,
          file_info: args.file_info,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, WARNING_DISK_CHECK_90])) {
          return;
        }
        const apiResult = {
          isDiskCheckWarning: response.data.code === WARNING_DISK_CHECK_90,
          photo_url: response.data.photo_url,
          binary_revision: response.data.binary_revision,
        }
        return apiResult;
      })
    },
    /** 蔵衛門アプリダウンロードURLリストAPI */
    async getAppDownloadUrlList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAppDownloadUrlList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getappdownloadurllist";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.app_info_list;
        context.commit('result/setAppInfoList', apiResult);
        return apiResult;
      })
    },
    /** WEBバージョン取得API */
    async getKuraemonCloudWebPageVersion(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonCloudWebPageVersion');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemoncloudwebpageversion";
        const data = {
          web_page_code: args.web_page_code,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.version;
      })
    },
    /** TODO (仮) 現在のサーバー時刻のタイムスタンプを返すAPI */
    async timestamp(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('timestamp');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/timestamp";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.timestamp;
      })
    },
    /** フリーメールドメイン判定API */
    async checkFreeMailDomain(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkFreeMailDomain');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checkfreemaildomain";
        const data = {
          domain_name: args.domain_name,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data;
      })
    },
    /** 黒板情報登録API */
    async registerKokubanInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerKokubanInfo');
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registerkokubaninfo";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kokuban_info: args.kokuban_info,
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data;
      })
    },
    /** 写真情報登録API */
    async registerPhotoInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerPhotoInfo');
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registerphotoinfo";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_info: args.photo_info,
          request_blob_upload: args.request_blob_upload,
          file_info: args.file_info,
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, WARNING_DISK_CHECK_90])) {
          return;
        }
        return {
          ...response.data,
          isDiskCheckWarning: response.data.code === WARNING_DISK_CHECK_90,
        };
      })
    },
    /** 図面リスト取得API */
    async getZumenList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getZumenList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getzumenlist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          last_modified_at: args.last_modified_at,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          construction_empty_folder_name_parts_list: response.data.construction_empty_folder_name_parts_list,
          zumen_info_list: response.data.zumen_info_list,
          done: response.data.done,
          next: response.data.next,
        }
        
        context.commit('result/setZumens', apiResult);
        return apiResult;
      })
    },
    /** 図面エンティティリスト取得API */
    async getZumenEntityList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getZumenEntityList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getzumenentitylist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          last_modified_at: args.last_modified_at,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          zumen_entity_list: response.data.zumen_entity_list,
          done: response.data.done,
          next: response.data.next,
        }
        
        context.commit('result/setZumenEntities', apiResult);
        return apiResult;
      })
    },
    /** 図面ページリスト取得API */
    async getZumenPageList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getZumenPageList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getzumenpagelist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          last_modified_at: args.last_modified_at,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          zumen_page_list: response.data.zumen_page_list,
          done: response.data.done,
          next: response.data.next,
        }
        
        context.commit('result/setZumenPages', apiResult);
        return apiResult;
      })
    },
    /** 空フォルダ作成API */
    async createZumenFolder(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createZumenFolder');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createzumenfolder";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          folder_name_parts: args.folder_name_parts,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** 図面作成API */
    async createZumen(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createZumen');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createzumen";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          folder_name_parts: args.folder_name_parts,
          file_name: args.file_name,
          file_info: args.file_info,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, WARNING_DISK_CHECK_90])) {
          return;
        }
        const apiResult = {
          isDiskCheckWarning: response.data.code === WARNING_DISK_CHECK_90,
          zumen_id: response.data.zumen_id,
          zumen_url: response.data.zumen_url,
        }
        
        context.commit('result/setZumenInfo', apiResult);
        return apiResult;
      })
    },
    /** 図面変更API */
    async modifyZumen(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('modifyZumen');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/modifyzumen";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          zumen_id: args.zumen_id,
          folder_name_parts: args.folder_name_parts,
          file_name: args.file_name,
          zumen_pages: args.zumen_pages,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** 図面フォルダ移動API */
    async moveZumenFolder(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('moveZumenFolder');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/movezumenfolder";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          from_folder_name_parts: args.from_folder_name_parts,
          to_folder_name_parts: args.to_folder_name_parts,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** 図面削除API */
    async deleteZumen(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('deleteZumen');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/deletezumen";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          zumen_ids: args.zumen_ids,
          folder_name_parts_list: args.folder_name_parts_list,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          done: response.data.done,
          next: response.data.next,
        }
        
        return apiResult;
      })
    },
    /** 図面復元API */
    async restoreZumen(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('restoreZumen');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/restorezumen";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          zumen_ids: args.zumen_ids,
          folder_name_parts_list: args.folder_name_parts_list,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          done: response.data.done,
          next: response.data.next,
        }
        
        return apiResult;
      })
    },
    /** 図面ビューワー起動トークン取得API */
    async getZumenViewerWakeupToken(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getZumenViewerWakeupToken');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getzumenviewerwakeuptoken";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          zumen_id: args.zumen_id,
          return_urls: args.return_urls,
          return_url: args.return_urls.close, // TODO 今後不要になる予定
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.zumen_viewer_wakeup_token;
      })
    },
    /** キャプチャ図面作成API */
    async createCaptureZumen(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createCaptureZumen');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createcapturezumen";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          zumen_id: args.zumen_id,
          zumen_entity_code: args.zumen_entity_code,
          zoom: args.zoom,
          left: args.left,
          top: args.top,
          width: args.width,
          height: args.height,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.capture_zumen_jpeg_url;
      })
    },
    /** お知らせ一覧取得API */
    async getNotificationList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getNotificationList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getnotificationlist";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.notification_list;
        return apiResult;
      })
    },
    /** サポートURL取得API */
    async getSupportUrl(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getSupportUrl');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getsupporturl";
        const data = {
          url_type: args.url_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.url;
      })
    },
    /** 解約アンケート登録API */
    async registerAnswerCancelQuestionnaire(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerAnswerCancelQuestionnaire');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeranswercancelquestionnaire";
        const data = {
          answer_info_list: args.answer_info_list,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK'
      })
    },
    /** 写真一括ダウンロード開始API */
    async startBulkDownloadPhoto(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('startBulkDownloadPhoto');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/startbulkdownloadphoto";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_keys: args.photo_keys,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          polling_millisec: response.data.polling_millisec,
          job_code: response.data.job_code,
        }
        return apiResult;
      })
    },
    /** 写真一括ダウンロードチェックAPI */
    async checkBulkDownloadPhoto(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('checkBulkDownloadPhoto');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/checkbulkdownloadphoto";
        const data = {
          job_code: args.job_code,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const CONTINUE_CODE = 201;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, CONTINUE_CODE])) {
          return;
        }
        if(response.data.code === CONTINUE_CODE){
          return {
            polling_millisec: response.data.polling_millisec,
            job_code: response.data.job_code,
            progress_percent: response.data.progress_percent,
          }
        }
        const apiResult = {
          download_url: response.data.download_url,
          file_name: response.data.file_name,
        }
        return apiResult;
      })
    },
    /** アップロード写真作成（WEB用）API */
    async createPhotoForWeb(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createPhotoForWeb');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createphotoforweb";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_list: args.photo_list
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const ONE_DAY_MAX_DISK_SIZE_OVER = 400017

        if (checkResponseError(context, response, descriptions, apiOptions, 
          [OK, WARNING_DISK_CHECK_90, ONE_DAY_MAX_DISK_SIZE_OVER])) {
          return;
        }

        const apiResult = {
          isDiskCheckWarning: response.data.code === WARNING_DISK_CHECK_90,
          isOneDayMaxDiskSizeOver: response.data.code === ONE_DAY_MAX_DISK_SIZE_OVER,
          web_upload_photo_token: response.data.web_upload_photo_token,
          upload_limit: response.data.upload_limit,
          photo_url_list: response.data.photo_url_list,
        }
        return apiResult;
      })
    },
    /** 写真アップロード完了登録（WEB用）API */
    async registerUploadPhotoFinishForWeb(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('registerUploadPhotoFinishForWeb');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/registeruploadphotofinishforweb";
        const data = {
          web_upload_photo_token: args.web_upload_photo_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** アップロード写真作成完了待ち（WEB用）API */
    async uploadPhotoPollingForWeb(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('uploadPhotoPollingForWeb');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/uploadphotopollingforweb";
        const data = {
          web_upload_photo_token: args.web_upload_photo_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const CONTINUE_CODE = 201000;
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, CONTINUE_CODE])) {
          return;
        }
        const apiResult = {
          photo_status_list: response.data.photo_status_list,
        }
        if(response.data.code === CONTINUE_CODE){
          apiResult.polling_millisec = response.data.polling_millisec
        }
        return apiResult;
      })
    },
    /** クラウド工事本棚情報ツリー取得API */
    async getCloudConstructionBookrackInfoTree(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getCloudConstructionBookrackInfoTree');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcloudconstructionbookrackinfotree";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          bookrack_info_tree: response.data.bookrack_info_tree,
          checkout_infos: response.data.checkout_infos,
          checkin_info: response.data.checkin_info,
        }
        context.commit('result/setBookrackInfo', apiResult);
        return apiResult;
      })
    },
    /** クラウド工事本棚アイテム存在確認API */
    async existsCloudConstructionBookrackItem(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('existsCloudConstructionBookrackItem');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/existscloudconstructionbookrackitem";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          bookrack_item_id: args.bookrack_item_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.item_exists;
      })
    },
    /** ビデオトークチャンネル取得API */
    async getVideoTalkChannel(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getVideoTalkChannel');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getvideotalkchannel";
        const data = {
          master_account_id: args.master_account_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          channel_name: response.data.channel_name,
          channel_arn: response.data.channel_arn,
          master_account_id: response.data.master_account_id,
          master_account_state: response.data.master_account_state,
          viewer_account_id: response.data.viewer_account_id,
          viewer_account_state: response.data.viewer_account_state,
          role_creds: response.data.role_creds,
          endpoint_info: response.data.endpoint_info,
        }
        return apiResult;
      })
    },
    /** ビデオトーク画面スクリーンショット写真作成API */
    async createPhotoVideoTalkScreenshot(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createPhotoVideoTalkScreenshot');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/createphotovideotalkscreenshot";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_info: args.photo_info,
          file_info: args.file_info,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, WARNING_DISK_CHECK_90])) {
          return;
        }
        const apiResult = {
          isDiskCheckWarning: response.data.code === WARNING_DISK_CHECK_90,
          photo_url: response.data.photo_url,
          binary_revision: response.data.binary_revision,
        }
        return apiResult;
      })
    },
    /** フォーム一覧取得API */
    async getFormList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getFormList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/getformlist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          alive_filter: args.alive_filter,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          form_list: response.data.form_list,
        }
        
        return apiResult;
      })
    },
    /** フォーム情報取得API */
    async getFormInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getFormInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/getforminfo";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.form_info;
      })
    },
    /** フォーム削除API */
    async deleteForm(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('deleteForm');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/deleteform";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_ids: args.kuraform_site_ids,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** フォーム復元API */
    async restoreForm(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('restoreForm');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/restoreform";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_ids: args.kuraform_site_ids,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** フォーム作成API */
    async createForm(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createForm');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/createform";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_app_kind: args.kuraform_app_kind,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        // 結果コード
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, 400037])) {
          return;
        }
        return response.data;
      })
    },
    /** フォーム編集API */
    async updateForm(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updateForm');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/updateform";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          kuraform_site_name: args.kuraform_site_name,
          kuraform_site_description: args.kuraform_site_description,
          kuraform_site_description_for_admin: args.kuraform_site_description_for_admin,
          kuraform_site_place: args.kuraform_site_place,
          item_list: args.item_list,
          item_display_order_list: args.item_display_order_list,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          continue_token: response.data.continue_token,
        }
        return apiResult;
      })
    },
    /** QRコードPDF作成API */
    async createFormQrcodePdf(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createFormQrcodePdf');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/createformqrcodepdf";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          column_id: args.column_id,
          continue_token: args.continue_token,
        }

        
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          created_report_url: response.data.created_report_url,
          continue_token: response.data.continue_token,
        }
        return apiResult;
      })
    },
    /** フォーム出力設定API */
    async updateFormOutputSetting(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updateFormOutputSetting');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/updateformoutputsetting";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          form_output_setting: args.form_output_setting
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** レポート作成API */
    async createFormReport(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('createFormReport');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/createformreport";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          report_kind: args.report_kind,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          created_report_url: response.data.created_report_url,
          created_report_mimetype: response.data.created_report_mimetype,
          continue_token: response.data.continue_token,
        }
        return apiResult;
      })
    },
    /** 回答一覧取得API */
    async getFormAnswerList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getFormAnswerList');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/getformanswerlist";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          last_record_id: args.last_record_id,
          limit: args.limit,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          column_list: response.data.column_list,
          kuraform_record_list: response.data.kuraform_record_list,
          kuraform_record_count: response.data.kuraform_record_count,
          column_info: response.data.column_info,
        }
        return apiResult;
      })
    },
    /** 回答削除API */
    async deleteFormAnswer(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('deleteFormAnswer');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/deleteformanswer";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          kuraform_record_ids: args.kuraform_record_ids,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** 一括新規レコード作成API */
    async bulkCreateNewRecord(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('bulkCreateNewRecord');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/bulkcreatenewrecord";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          file_info: args.file_info,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          bulk_create_new_record_token: response.data.bulk_create_new_record_token,
          csv_url: response.data.csv_url,
        }
        
        return apiResult;
      })
    },
    /** 一括新規レコード作成状況チェックAPI  */
    async bulkCreateNewRecordPolling(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('bulkCreateNewRecordPolling');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/bulkcreatenewrecordpolling";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          bulk_create_new_record_token: args.bulk_create_new_record_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        const CONTINUE_CODE = 201000;
        //  400039:アップロードしたCSVファイルの件数が1000件を超えている
        if (checkResponseError(context, response, descriptions, apiOptions, [OK, CONTINUE_CODE, 400039])) {
          return;
        }
        return response.data;
      })
    },
    /** 回答レコード編集API */
    async updateFormAnswerRecord(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updateFormAnswerRecord');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/kuraform/updateformanswerrecord";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kuraform_site_id: args.kuraform_site_id,
          kuraform_record_id: args.kuraform_record_id,
          update_item: args.update_item,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },

    /** 蔵衛門黒板V1セット取得API */
    async getKokubanSet(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKokubanSet');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkokubanset";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kokuban_hashes: args.kokuban_hashes,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.kokuban_info_list;
      })
    },
    /** 写真一覧ページ構造取得API */
    async getPhotoViewStructure(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getPhotoViewStructure');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getphotoviewstructure";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          view: args.view,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data;
      })
    },
    /** 黒板一覧ページ構造取得API */
    async getKokubanViewStructure(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKokubanViewStructure');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkokubanviewstructure";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          view: args.view,
          continue_token: args.continue_token,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return response.data;
      })
    },
    /** クラウド工事情報を「result」に設定する */
    async setCloudConstructionInfo(context, cloudConstruction) {
      context.commit('result/setCloudConstructionInfo', cloudConstruction);
    },

    /** トーク通知メール設定取得API */
    async getTalkNotificationMailSetting(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTalkNotificationMailSetting');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettalknotificationmailsetting";
        const data = {
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return { talk_notification_mail_enabled: response.data.talk_notification_mail_enabled };
      })
    },
    /** トーク通知メール設定更新API */
    async setTalkNotificationMailSetting(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('setTalkNotificationMailSetting');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/settalknotificationmailsetting";
        const data = {
          talk_notification_mail_enabled: args.talk_notification_mail_enabled,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },
    /** Get photo organize info tree API */
    async getPhotoOrganizeInfoTree(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getPhotoOrganizeInfoTree');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getphotoorganizeinfotree";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          photo_organize_info_tree_list: response.data.photo_organize_info_tree_list
        }
        return apiResult;
      })
    },
    /** Update photo organize info tree API */
    async updatePhotoOrganizeInfoTree(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('updatePhotoOrganizeInfoTree');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/updatephotoorganizeinfotree";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          photo_organize_info_tree_list: args.photo_organize_info_tree_list,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          update_photo_organize_info_tree_list: response.data.update_photo_organize_info_tree_list
        }
        return apiResult;
      })
    },

    /** 取り込みマスタ(TreeSource)保存API */
    async saveTreeSource(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('saveTreeSource');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/savetreesource";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          kind: args.kind,
          file_name: args.file_name,
          nodes: args.nodes, // {t, v, c} 形のノード配列(森)
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return { tree_source: response.data.tree_source };
      })
    },

    /** 取り込みマスタ(TreeSource)取得API */
    async getTreeSource(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTreeSource');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettreesource";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          source_tree_id: args.source_tree_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return { tree_source: response.data.tree_source };
      })
    },

    /** 取り込みマスタ(TreeSource)一覧取得API（工事単位・メタデータのみ） */
    async getTreeSourceListByConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTreeSourceListByConstruction');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettreesourcelistbyconstruction";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return { tree_source_list: response.data.tree_source_list };
      })
    },

    /** 取り込みマスタ(TreeSource)削除API */
    async deleteTreeSource(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('deleteTreeSource');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/deletetreesource";
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          source_tree_id: args.source_tree_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        return 'OK';
      })
    },

    /** amnimoテスト */
    async getAmnimoCameraList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAmnimoCameraList');

      console.info('getAmnimoCameraList')

      return await apiCall(context, apiOptions, async (apiConfig) => {
        console.info('useAPI')
        const url = API_ORIGIN + "/getamnimocameralist";
        const data = {
          account_type:args.account_type
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        console.info(response)

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        console.info(response.data.cameras)
        context.commit('result/setAmnimoCameraList', response.data.cameras);
        context.commit('result/setAmnimoTokenInfo', response.data.token_info );
        context.commit('result/setAmnimoHls', null);

        return { token_info: response.data.token_info, cameras: response.data.cameras };
      })
    },

    /** amnimoテスト */
    async getAmnimoHls(context, args) {
      context.commit('result/setAmnimoHls', null);
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAmnimoHLls');

      console.info('getAmnimoHls')

      return await apiCall(context, apiOptions, async (apiConfig) => {
        console.info('useAPI')
        const url = API_ORIGIN + "/getamnimocamerahls";
        const data = {
          'access_token': args.access_token,
          'camera_id': args.camera_id,
          'start_time': args.start_time,
          'account_type': args.account_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        console.info(response)
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        context.commit('result/setAmnimoHls', response.data.hls_info);

        return response.data.hls_info;
      })
    },

    async makeAmnimoSnapShot(context, args) {
      context.commit('result/setAmnimoSnapShot', null);
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('makeAmnimoSnapShot');

      console.info('makeAmnimoSnapShot')

      return await apiCall(context, apiOptions, async (apiConfig) => {
        console.info('useAPI')
        const url = API_ORIGIN + "/makeamnimosnapshot";
        const data = {
          'access_token': args.access_token,
          'camera_id': args.camera_id,
          'start_time': args.start_time,
          'account_type': args.account_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        console.info(response)
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        context.commit('result/setAmnimoSnapShot', response.data.snapshot_info);

        return response.data.snapshot_info;
      })
    },

    async getAmnimoSnapShot(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getAmnimoSnapShot');

      console.info('getAmnimoSnapShot')

      return await apiCall(context, apiOptions, async (apiConfig) => {
        console.info('useAPI')
        const url = API_ORIGIN + "/getamnimosnapshot";
        const data = {
          'access_token': args.access_token,
          'camera_id': args.camera_id,
          'media_id': args.media_id,
          'account_type': args.account_type,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        console.info(response)
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        console.info(response.data.snapshot_info)
        context.commit('result/setAmnimoSnapShot', response.data.snapshot_info);

        return response.data.snapshot_info;
      })
    },

    async makeKuraemonLiveSnapshot(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('makeKuraemonLiveSnapshot');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/makekuraemonlivesnapshot";
        const data = {
          amnimo_camera_id: args.camera_id,
          start_time: args.start_time,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          snapshot: response.data.snapshot,
        }
        return apiResult;
      })
    },

    async makeKuraemonLiveVideoClip(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('makeKuraemonLiveVideoClip');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/makekuraemonlivevideoclip";
        const data = {
          amnimo_camera_id: args.camera_id,
          start_time: args.start_time,
          end_time: args.end_time,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          snapshot: response.data.snapshot,
        }
        return apiResult;
      })
    },

    async getKuraemonLiveSnapshot(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveSnapshot');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivesnapshot";
        const data = {
          amnimo_camera_id: args.camera_id,
          media_id: args.media_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          snapshot: response.data.snapshot,
        }
        return apiResult;
      })
    },

    ////////////////////////////
    // kuraemonLive
    async getKuraemonLiveList(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveList');
      
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivelist";
        const data = {
          construction_id: args.construction_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401035: `カメラの読み込みに失敗しました\n\n画面を再読み込みしても改善しない場合は、`,
          500: `カメラの読み込みに失敗しました`,
        }
        
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          kuraemon_live_list: response.data.kuraemon_live_list.map(item => ({
            ...item,
          })),
          isCallButtonEarlyRelease: response.data.is_call_button_early_release || false,
        }

        context.commit('result/setKuraemonLiveList', apiResult.kuraemon_live_list);
        context.commit('result/setIsCallButtonEarlyRelease', apiResult.isCallButtonEarlyRelease);
        return apiResult;
      })
    },

    async getKuraemonLiveHls(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveHls');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivehls";
        const data = {
          amnimo_camera_id: args.camera_id,
          start_time: args.start_time,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        
        const target = data.start_time ? 'ビデオ' : 'LIVE';
        const descriptions = {
          ...apiDefaultDescriptions,
          401035: `${target}の読み込みに失敗しました\n\n画面を再読み込みしても改善しない場合は、`,
          500: `${target}を表示できませんでした`,
        }
        
        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          hls_info: response.data.hls_info,
        }
        return apiResult;
      })
    },

    async getKuraemonLiveThumbnails(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveThumbnails');
      const append = args.__append === true;

      if (!append) {
        context.commit('result/setKuraemonLiveThumbnails', []);//初期化
      }
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivethumbnails";
        const data = {
          amnimo_camera_id: args.camera_id,
          target_at_list: args.target_at_list,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401035: `ビデオの読み込みに失敗しました\n\n繰り返し表示される場合は、`,
          500: `ビデオの読み込みに失敗しました`,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          kuraemon_live_thumbnails: response.data.kuraemon_live_thumbnails,
        }
        if (append) {
          // 追記モード: 既存の結果に追加
          const existing = context.getters['result/kuraemonLiveThumbnails'];
          context.commit('result/setKuraemonLiveThumbnails', [...existing, ...apiResult.kuraemon_live_thumbnails]);
        } else {
          const kuraemonLiveThumbnails = context.getters['result/kuraemonLiveThumbnails'];
          if (kuraemonLiveThumbnails.length == 0) {
            // 初期化状態のみコミット,初期化状態出ない場合は通信中に他の結果が先に保存された可能性が高い
            context.commit('result/setKuraemonLiveThumbnails', apiResult.kuraemon_live_thumbnails);
          }
        }

        return apiResult;
      })
    },

    async getKuraemonLiveDayHistory(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveDayHistory');

      context.commit('result/setKuraemonLiveHistory', []);//初期化
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivedayhistory";
        const data = {
          amnimo_camera_id: args.camera_id,
          target_date: args.target_date,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
          401035: `ビデオの読み込みに失敗しました\n\n繰り返し表示される場合は、`,
          500: `ビデオの読み込みに失敗しました`,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          kuraemon_live_history: response.data.kuraemon_live_history,
        }
        const kuraemonLiveHistory = context.getters['result/kuraemonLiveHistory'];
        if (kuraemonLiveHistory.length == 0) {
          // 初期化状態のみコミット,初期化状態出ない場合は通信中に他の結果が先に保存された可能性が高い
          context.commit('result/setKuraemonLiveHistory', apiResult.kuraemon_live_history);
        }

        return apiResult;
      })
    },

    async getKuraemonLiveRecodingCalender(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveRecodingCalender');

      context.commit('result/setKuraemonLiveRecodingCalender', []);//初期化
      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonliverecodingcalender";
        const data = {
          amnimo_camera_id: args.camera_id,
          start_day: args.start_day,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        const descriptions = {
          ...apiDefaultDescriptions,
          401035: `ビデオの読み込みに失敗しました\n\n繰り返し表示される場合は、`,
          500: `ビデオの読み込みに失敗しました`, 
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          recoding_calenders: response.data.recoding_calenders,
        }
        context.commit('result/setKuraemonLiveRecodingCalender', apiResult.recoding_calenders);

        return apiResult;
      })
    },

    async changeKuraemonLiveName(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('changeKuraemonLiveName');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/changekuraemonlivename";
        const data = {
          amnimo_camera_id: args.camera_id,
          camera_name: args.camera_name,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          kuraemon_live: response.data.kuraemon_live,
        }
        return apiResult;
      })
    },

    async getKuraemonLiveMqttInfo(context, args) {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getKuraemonLiveMqttInfo');

      return await apiCall(context, apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getkuraemonlivemqttinfo";
        const data = {
          amnimo_camera_id: args.camera_id,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(context, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          access_token: response.data.access_token,
          user_name: response.data.user_name,
          websocket_url: response.data.websocket_url,
          thing_name: response.data.thing_name,
        }
        return apiResult;
      })
    },

  },
}
